import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';

import type { KpiDataRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { getTrackPercent } from 'components/molecules/PackArchitecture/kpiRangeSlider.utils';

const TICK_LABEL_CLASS = 'absolute -translate-x-1/2 text-[10px] leading-4 text-kpiSliderTick';

const BREAKPOINT_LABEL_CLASS =
    'absolute -translate-x-1/2 rounded bg-purpleFont px-1.5 text-[10px] leading-4 text-white';

type RangeHandleProps = {
    positionPercent: number;
    isActive: boolean;
    label: string;
    onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
};

const RangeHandle = ({ positionPercent, isActive, label, onPointerDown }: RangeHandleProps) => (
    <button
        type="button"
        className={`absolute top-1/2 z-10 flex h-[38px] w-[10px] -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-[2px] rounded-[6px] border border-breadcrumbSlash bg-white py-[2px] focus:outline-none ${
            isActive ? 'cursor-grabbing shadow-[0_0_0_2px_rgba(0,0,0,0.04)]' : 'cursor-grab'
        }`}
        style={{ left: `${positionPercent}%` }}
        onPointerDown={onPointerDown}
        aria-label={label}
    >
        <span className="h-[17px] w-px rounded-full bg-sliderHandleGrip" />
        <span className="h-[17px] w-px rounded-full bg-sliderHandleGrip" />
    </button>
);

type BreakpointHandleProps = {
    positionPercent: number;
    isActive: boolean;
    value: number;
    onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
};

const BreakpointHandle = ({ positionPercent, isActive, value, onPointerDown }: BreakpointHandleProps) => (
    <button
        type="button"
        className={`absolute top-1/2 z-10 flex h-8 w-[9px] -translate-x-1/2 -translate-y-1/2 items-center justify-center focus:outline-none ${
            isActive ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        style={{ left: `${positionPercent}%` }}
        onPointerDown={onPointerDown}
        aria-label={`Breakpoint ${value}`}
    >
        <span className="h-full w-[3px] rounded-sm bg-purpleFont" />
    </button>
);

type KpiRangeSliderTrackProps = {
    dataRange: KpiDataRange;
    min: number;
    max: number;
    minPercent: number;
    maxPercent: number;
    isFullRange: boolean;
    aboveLabels: number[];
    dividers: number[];
    trackRef: RefObject<HTMLDivElement>;
    activeHandle: 'min' | 'max' | null;
    onStartDrag: (handle: 'min' | 'max') => (event: ReactPointerEvent<HTMLButtonElement>) => void;
    isCustomBinning?: boolean;
    draggableBreakpoints?: number[];
    activeBreakpointIndex?: number | null;
    onStartBreakpointDrag?: (index: number) => (event: ReactPointerEvent<HTMLButtonElement>) => void;
};

const KpiRangeSliderTrack = ({
    dataRange,
    min,
    max,
    minPercent,
    maxPercent,
    isFullRange: _isFullRange,
    aboveLabels,
    dividers,
    trackRef,
    activeHandle,
    onStartDrag,
    isCustomBinning = false,
    draggableBreakpoints = [],
    activeBreakpointIndex = null,
    onStartBreakpointDrag,
}: KpiRangeSliderTrackProps) => (
    <>
        <div className="relative h-4 mb-[3px]">
            <div className="absolute inset-x-[5px] top-0 h-full">
                {aboveLabels.map((value) => (
                    <span
                        key={value}
                        className={isCustomBinning ? BREAKPOINT_LABEL_CLASS : TICK_LABEL_CLASS}
                        style={{ left: `${getTrackPercent(value, dataRange)}%` }}
                    >
                        {value}
                    </span>
                ))}
                {min !== dataRange.defaultMin && (
                    <span
                        className="absolute -translate-x-1/2 rounded bg-kpiRangeLabel px-1.5 text-[10px] leading-4 text-white"
                        style={{ left: `${minPercent}%` }}
                    >
                        {min}
                    </span>
                )}
                {max !== dataRange.defaultMax && (
                    <span
                        className="absolute -translate-x-1/2 rounded bg-kpiRangeLabel px-1.5 text-[10px] leading-4 text-white"
                        style={{ left: `${maxPercent}%` }}
                    >
                        {max}
                    </span>
                )}
            </div>
        </div>

        <div className="relative h-[38px]">
            <div className="absolute inset-x-[5px] top-0 h-full">
                <div
                    ref={trackRef}
                    className="absolute inset-x-0 top-1/2 h-8 -translate-y-1/2 overflow-hidden rounded bg-backgroundGreyAlt"
                >
                    <div
                        className="pointer-events-none absolute top-0 h-full bg-bgBackground"
                        style={{ left: `${minPercent}%`, width: `${Math.max(maxPercent - minPercent, 0)}%` }}
                    />
                    {!isCustomBinning &&
                        dividers.map((value) => (
                            <div
                                key={value}
                                className="pointer-events-none absolute top-0 h-full w-px -translate-x-1/2 bg-kpiSliderTick"
                                style={{ left: `${getTrackPercent(value, dataRange)}%` }}
                            />
                        ))}
                </div>

                <RangeHandle
                    positionPercent={minPercent}
                    isActive={activeHandle === 'min'}
                    label="Minimum range handle"
                    onPointerDown={onStartDrag('min')}
                />
                {draggableBreakpoints.map((value, index) => (
                    <BreakpointHandle
                        key={`${value}-${index}`}
                        positionPercent={getTrackPercent(value, dataRange)}
                        isActive={activeBreakpointIndex === index}
                        value={value}
                        onPointerDown={
                            onStartBreakpointDrag
                                ? onStartBreakpointDrag(index)
                                : (event) => event.preventDefault()
                        }
                    />
                ))}
                <RangeHandle
                    positionPercent={maxPercent}
                    isActive={activeHandle === 'max'}
                    label="Maximum range handle"
                    onPointerDown={onStartDrag('max')}
                />
            </div>
        </div>

        <div className="relative  h-4">
            <span className="absolute left-0 text-[10px] leading-4 text-toolbarLabelGrey">
                {dataRange.defaultMin}
            </span>
            <span className="absolute right-0 text-[10px] leading-4 text-toolbarLabelGrey">
                {dataRange.defaultMax}
            </span>
        </div>
    </>
);

export default KpiRangeSliderTrack;
